import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { 
  CircleNotch,
  Receipt,
  Buildings,
  FileText,
  Calculator,
  TrendUp,
  TrendDown,
  CheckCircle,
  Warning,
  XCircle,
  Download,
  CalendarBlank,
  Gear,
  CurrencyCircleDollar,
  ShieldCheck,
  Lock,
  ArrowRight,
  LightbulbFilament,
  ClipboardText,
  Clock,
  ArrowSquareOut,
  CaretRight,
  Sparkle,
  ChartLineUp,
  Gauge,
  Bell,
  Plus,
  Eye,
  MagnifyingGlass
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UAE VAT Rate
const UAE_VAT_RATE = 5;

export default function TaxCompliancePage() {
  const { user, hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vat-return');
  const [loading, setLoading] = useState(false);
  
  // VAT Settings State
  const [vatSettings, setVatSettings] = useState({
    trn: '',
    vat_registered: false,
    vat_filing_frequency: 'quarterly',
    is_free_zone: false,
    free_zone_name: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // VAT Return State
  const [vatReturn, setVatReturn] = useState(null);
  const [vatPeriodStart, setVatPeriodStart] = useState(
    new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1).toISOString().split('T')[0]
  );
  const [vatPeriodEnd, setVatPeriodEnd] = useState(
    new Date().toISOString().split('T')[0]
  );
  
  // Corporate Tax State
  const [corporateTax, setCorporateTax] = useState(null);
  const [fiscalYearStart, setFiscalYearStart] = useState(
    `${new Date().getFullYear()}-01-01`
  );
  const [fiscalYearEnd, setFiscalYearEnd] = useState(
    `${new Date().getFullYear()}-12-31`
  );
  
  // VAT Transactions State
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);

  // Validation State
  const [validationSettings, setValidationSettings] = useState(null);
  const [vatValidationResult, setVatValidationResult] = useState(null);
  const [ctValidationResult, setCtValidationResult] = useState(null);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationType, setValidationType] = useState('vat');
  
  // Filing State
  const [showFilingDialog, setShowFilingDialog] = useState(false);
  const [filingTaxType, setFilingTaxType] = useState('vat');
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);
  const [filingNotes, setFilingNotes] = useState('');
  const [filing, setFiling] = useState(false);
  const [periodStatus, setPeriodStatus] = useState(null);
  
  // Deadline State
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [newDeadline, setNewDeadline] = useState({
    tax_type: 'vat',
    period_start: '',
    period_end: '',
    deadline_date: '',
    reminder_days: 7
  });
  
  // Filings History
  const [filings, setFilings] = useState([]);
  const [showFilingsHistory, setShowFilingsHistory] = useState(false);
  
  // Enterprise Analytics
  const [riskScore, setRiskScore] = useState(null);
  const [complianceHealth, setComplianceHealth] = useState(null);

  const canView = hasPermission('accounts', 'view');
  const canEdit = hasPermission('accounts', 'edit');

  useEffect(() => {
    fetchVatSettings();
    fetchValidationSettings();
    fetchUpcomingDeadlines();
  }, []);

  const fetchVatSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tax/vat-settings`);
      setVatSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch VAT settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchValidationSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/tax-validation/settings`);
      setValidationSettings(response.data);
      
      // Fetch enterprise analytics if available
      if (response.data.plan_features?.risk_scoring) {
        fetchRiskScore();
      }
      if (response.data.plan_features?.advanced_analytics) {
        fetchComplianceHealth();
      }
    } catch (error) {
      console.error('Failed to fetch validation settings:', error);
    }
  };

  const fetchRiskScore = async () => {
    try {
      const response = await axios.get(`${API_URL}/tax-validation/analytics/risk-score`);
      setRiskScore(response.data);
    } catch (error) {
      console.error('Risk score not available:', error);
    }
  };

  const fetchComplianceHealth = async () => {
    try {
      const response = await axios.get(`${API_URL}/tax-validation/analytics/health`);
      setComplianceHealth(response.data);
    } catch (error) {
      console.error('Compliance health not available:', error);
    }
  };

  const fetchUpcomingDeadlines = async () => {
    try {
      const response = await axios.get(`${API_URL}/tax-validation/upcoming-deadlines`);
      setUpcomingDeadlines(response.data);
    } catch (error) {
      console.error('Failed to fetch deadlines:', error);
    }
  };

  const fetchFilings = async () => {
    try {
      const response = await axios.get(`${API_URL}/tax-validation/filings`);
      setFilings(response.data);
      setShowFilingsHistory(true);
    } catch (error) {
      toast.error('Failed to fetch filings history');
    }
  };

  const checkPeriodStatus = async (taxType, start, end) => {
    try {
      const params = new URLSearchParams({ tax_type: taxType, period_start: start, period_end: end });
      const response = await axios.get(`${API_URL}/tax-validation/period-status?${params}`);
      setPeriodStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check period status:', error);
      return null;
    }
  };

  const saveVatSettings = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/tax/vat-settings`, vatSettings);
      toast.success('VAT settings saved successfully');
    } catch (error) {
      toast.error('Failed to save VAT settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVatReturn = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period_start: vatPeriodStart,
        period_end: vatPeriodEnd
      });
      const response = await axios.get(`${API_URL}/tax/vat-return?${params}`);
      setVatReturn(response.data);
      
      // Check period status
      await checkPeriodStatus('vat', vatPeriodStart, vatPeriodEnd);
    } catch (error) {
      toast.error('Failed to fetch VAT return data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVatTransactions = async () => {
    try {
      const params = new URLSearchParams({
        period_start: vatPeriodStart,
        period_end: vatPeriodEnd
      });
      const response = await axios.get(`${API_URL}/tax/vat-transactions?${params}`);
      setTransactions(response.data.transactions || []);
      setShowTransactions(true);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchCorporateTax = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        fiscal_year_start: fiscalYearStart,
        fiscal_year_end: fiscalYearEnd
      });
      const response = await axios.get(`${API_URL}/tax/corporate-tax?${params}`);
      setCorporateTax(response.data);
      
      // Check period status
      await checkPeriodStatus('corporate_tax', fiscalYearStart, fiscalYearEnd);
    } catch (error) {
      toast.error('Failed to calculate corporate tax');
    } finally {
      setLoading(false);
    }
  };

  // Validation Functions
  const runVatValidation = async () => {
    setValidating(true);
    setValidationType('vat');
    setShowValidationPanel(true);
    
    try {
      const params = new URLSearchParams({
        period_start: vatPeriodStart,
        period_end: vatPeriodEnd
      });
      const response = await axios.post(`${API_URL}/tax-validation/validate-vat?${params}`);
      setVatValidationResult(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Upgrade required for VAT validation');
      } else {
        toast.error('Validation failed');
      }
      setShowValidationPanel(false);
    } finally {
      setValidating(false);
    }
  };

  const runCorporateTaxValidation = async () => {
    setValidating(true);
    setValidationType('corporate_tax');
    setShowValidationPanel(true);
    
    try {
      const params = new URLSearchParams({
        fiscal_year_start: fiscalYearStart,
        fiscal_year_end: fiscalYearEnd
      });
      const response = await axios.post(`${API_URL}/tax-validation/validate-corporate-tax?${params}`);
      setCtValidationResult(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Upgrade required for Corporate Tax validation');
      } else {
        toast.error('Validation failed');
      }
      setShowValidationPanel(false);
    } finally {
      setValidating(false);
    }
  };

  const handleFileTaxReturn = async () => {
    setFiling(true);
    const validationResult = validationType === 'vat' ? vatValidationResult : ctValidationResult;
    
    try {
      await axios.post(`${API_URL}/tax-validation/file`, {
        tax_type: filingTaxType,
        period_start: filingTaxType === 'vat' ? vatPeriodStart : fiscalYearStart,
        period_end: filingTaxType === 'vat' ? vatPeriodEnd : fiscalYearEnd,
        validation_result: validationResult,
        notes: filingNotes,
        acknowledged_warnings: acknowledgedWarnings
      });
      
      toast.success('Tax return filed successfully! Period is now locked.');
      setShowFilingDialog(false);
      setShowValidationPanel(false);
      setFilingNotes('');
      setAcknowledgedWarnings(false);
      
      // Refresh period status
      if (filingTaxType === 'vat') {
        await checkPeriodStatus('vat', vatPeriodStart, vatPeriodEnd);
      } else {
        await checkPeriodStatus('corporate_tax', fiscalYearStart, fiscalYearEnd);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file tax return');
    } finally {
      setFiling(false);
    }
  };

  const createDeadline = async () => {
    try {
      await axios.post(`${API_URL}/tax-validation/deadlines`, newDeadline);
      toast.success('Deadline created and added to calendar');
      setShowDeadlineDialog(false);
      fetchUpcomingDeadlines();
      setNewDeadline({
        tax_type: 'vat',
        period_start: '',
        period_end: '',
        deadline_date: '',
        reminder_days: 7
      });
    } catch (error) {
      toast.error('Failed to create deadline');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getValidationResult = () => validationType === 'vat' ? vatValidationResult : ctValidationResult;
  
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
      case 'warning': return 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
      case 'passed': return 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'error': return <XCircle size={18} weight="fill" className="text-red-500" />;
      case 'warning': return <Warning size={18} weight="fill" className="text-amber-500" />;
      case 'passed': return <CheckCircle size={18} weight="fill" className="text-emerald-500" />;
      default: return null;
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">You don't have permission to view tax compliance.</p>
      </div>
    );
  }

  // Validation Panel Component
  const ValidationPanel = () => {
    const result = getValidationResult();
    if (!result && !validating) return null;

    const progressPercent = result ? (result.passed_checks / result.total_checks) * 100 : 0;
    const hasErrors = result?.errors?.length > 0;
    const hasWarnings = result?.warnings?.length > 0;

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className="fixed right-0 top-0 h-screen w-full max-w-lg bg-background border-l shadow-2xl z-50 overflow-y-auto"
      >
        <div className="sticky top-0 bg-background border-b p-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShieldCheck size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-semibold">Validation Checklist</h2>
                <p className="text-xs text-muted-foreground">
                  {validationType === 'vat' ? 'VAT Return' : 'Corporate Tax'} Validation
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowValidationPanel(false)}>
              <XCircle size={20} />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {validating ? (
            <div className="flex flex-col items-center justify-center py-20">
              <CircleNotch size={48} className="animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Running validation checks...</p>
            </div>
          ) : result ? (
            <>
              {/* Progress Summary */}
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Validation Progress</span>
                  <span className="text-sm font-bold">{result.passed_checks}/{result.total_checks} passed</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <span className="flex items-center gap-1">
                    <XCircle size={14} className="text-red-500" /> {result.errors.length} errors
                  </span>
                  <span className="flex items-center gap-1">
                    <Warning size={14} className="text-amber-500" /> {result.warnings.length} warnings
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle size={14} className="text-emerald-500" /> {result.passed.length} passed
                  </span>
                </div>
              </div>

              {/* Errors Section */}
              {hasErrors && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-red-600 flex items-center gap-2">
                    <XCircle size={16} weight="fill" /> Errors (Blocking)
                  </h3>
                  <Accordion type="multiple" className="space-y-2">
                    {result.errors.map((check, idx) => (
                      <AccordionItem key={check.id} value={check.id} className={`border rounded-lg ${getSeverityColor('error')}`}>
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            {getSeverityIcon('error')}
                            <div>
                              <p className="font-medium text-sm">{check.name}</p>
                              <p className="text-xs text-muted-foreground">{check.details}</p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-3 pt-2 border-t">
                            <p className="text-sm">{check.description}</p>
                            {check.fix_suggestion && (
                              <div className="p-3 rounded bg-white dark:bg-slate-900 border">
                                <div className="flex items-start gap-2">
                                  <LightbulbFilament size={16} className="text-amber-500 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-xs font-medium mb-1">Suggested Fix</p>
                                    <p className="text-sm text-muted-foreground">{check.fix_suggestion}</p>
                                  </div>
                                </div>
                                {check.fix_link && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="mt-2 w-full"
                                    onClick={() => navigate(check.fix_link)}
                                  >
                                    <ArrowSquareOut size={14} className="mr-1" /> Go to Fix
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Warnings Section */}
              {hasWarnings && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-amber-600 flex items-center gap-2">
                    <Warning size={16} weight="fill" /> Warnings (Non-blocking)
                  </h3>
                  <Accordion type="multiple" className="space-y-2">
                    {result.warnings.map((check, idx) => (
                      <AccordionItem key={check.id} value={check.id} className={`border rounded-lg ${getSeverityColor('warning')}`}>
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            {getSeverityIcon('warning')}
                            <div>
                              <p className="font-medium text-sm">{check.name}</p>
                              <p className="text-xs text-muted-foreground">{check.details}</p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-3 pt-2 border-t">
                            <p className="text-sm">{check.description}</p>
                            {check.fix_suggestion && (
                              <div className="p-3 rounded bg-white dark:bg-slate-900 border">
                                <div className="flex items-start gap-2">
                                  <LightbulbFilament size={16} className="text-amber-500 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-xs font-medium mb-1">Suggested Fix</p>
                                    <p className="text-sm text-muted-foreground">{check.fix_suggestion}</p>
                                  </div>
                                </div>
                                {check.fix_link && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="mt-2 w-full"
                                    onClick={() => navigate(check.fix_link)}
                                  >
                                    <ArrowSquareOut size={14} className="mr-1" /> Go to Fix
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Passed Section */}
              {result.passed.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-emerald-600 flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" /> Passed Checks
                  </h3>
                  <div className="space-y-2">
                    {result.passed.map((check) => (
                      <div key={check.id} className={`p-3 rounded-lg border ${getSeverityColor('passed')}`}>
                        <div className="flex items-center gap-3">
                          {getSeverityIcon('passed')}
                          <div>
                            <p className="font-medium text-sm">{check.name}</p>
                            <p className="text-xs text-muted-foreground">{check.details}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="sticky bottom-0 pt-4 pb-2 bg-background border-t">
                {result.can_submit ? (
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      setFilingTaxType(validationType);
                      setShowFilingDialog(true);
                    }}
                  >
                    <FileText size={16} className="mr-2" />
                    {hasWarnings ? 'Proceed with Warnings' : 'File Tax Return'}
                  </Button>
                ) : (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-center">
                    <Lock size={20} className="mx-auto text-red-500 mb-2" />
                    <p className="text-sm font-medium text-red-600">Filing Blocked</p>
                    <p className="text-xs text-red-500">Resolve all errors before filing</p>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Validation Panel Overlay */}
      <AnimatePresence>
        {showValidationPanel && <ValidationPanel />}
      </AnimatePresence>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Receipt className="text-primary" size={28} />
            UAE Tax Compliance
          </h1>
          <p className="text-muted-foreground">VAT Filing & Corporate Tax Management</p>
        </div>
        
        <div className="flex items-center gap-3">
          {vatSettings.trn && (
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <CheckCircle className="text-emerald-600" size={18} weight="fill" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                TRN: {vatSettings.trn}
              </span>
            </div>
          )}
          {validationSettings && (
            <Badge className={
              validationSettings.subscription_plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
              validationSettings.subscription_plan === 'pro' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }>
              {validationSettings.subscription_plan?.toUpperCase()}
            </Badge>
          )}
        </div>
      </motion.div>

      {/* Alerts & Deadlines Bar */}
      {upcomingDeadlines.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
        >
          <Bell size={20} className="text-amber-600" weight="fill" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Upcoming Deadline: {upcomingDeadlines[0].tax_type.toUpperCase()} filing due {formatDate(upcomingDeadlines[0].deadline_date)}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowDeadlineDialog(true)}>
            View All
          </Button>
        </motion.div>
      )}

      {/* Enterprise Analytics Cards */}
      {(riskScore || complianceHealth) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {riskScore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-tactile rounded-lg p-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${
                  riskScore.risk_level === 'low' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                  riskScore.risk_level === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30' :
                  'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <Gauge size={20} className={
                    riskScore.risk_level === 'low' ? 'text-emerald-600' :
                    riskScore.risk_level === 'medium' ? 'text-amber-600' :
                    'text-red-600'
                  } />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Risk Score</p>
                  <p className="text-xl font-bold">{riskScore.risk_score}/100</p>
                </div>
              </div>
              <Badge className={
                riskScore.risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' :
                riskScore.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }>
                {riskScore.risk_level?.toUpperCase()} RISK
              </Badge>
            </motion.div>
          )}
          
          {complianceHealth && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-tactile rounded-lg p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <FileText size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Filings</p>
                    <p className="text-xl font-bold">{complianceHealth.total_filings}</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-tactile rounded-lg p-4"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${complianceHealth.overdue_deadlines > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                    <Clock size={20} className={complianceHealth.overdue_deadlines > 0 ? 'text-red-600' : 'text-emerald-600'} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Pending Deadlines</p>
                    <p className="text-xl font-bold">{complianceHealth.pending_deadlines}</p>
                    {complianceHealth.overdue_deadlines > 0 && (
                      <p className="text-xs text-red-500">{complianceHealth.overdue_deadlines} overdue</p>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="vat-return" className="gap-2" data-testid="vat-return-tab">
            <FileText size={16} />
            VAT Return
          </TabsTrigger>
          <TabsTrigger value="corporate-tax" className="gap-2" data-testid="corporate-tax-tab">
            <Buildings size={16} />
            Corporate Tax
          </TabsTrigger>
          <TabsTrigger value="filings" className="gap-2" data-testid="filings-tab">
            <ClipboardText size={16} />
            Filings
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2" data-testid="tax-settings-tab">
            <Gear size={16} />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* VAT Return Tab */}
        <TabsContent value="vat-return" className="mt-6 space-y-6">
          {/* Period Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-4"
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Period Start</Label>
                <Input
                  type="date"
                  value={vatPeriodStart}
                  onChange={(e) => setVatPeriodStart(e.target.value)}
                  className="w-40"
                  data-testid="vat-period-start"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Period End</Label>
                <Input
                  type="date"
                  value={vatPeriodEnd}
                  onChange={(e) => setVatPeriodEnd(e.target.value)}
                  className="w-40"
                  data-testid="vat-period-end"
                />
              </div>
              <Button onClick={fetchVatReturn} disabled={loading} data-testid="calculate-vat-btn">
                {loading ? <CircleNotch className="animate-spin" size={16} /> : <Calculator size={16} className="mr-2" />}
                Calculate VAT
              </Button>
              {vatReturn && (
                <>
                  <Button variant="outline" onClick={fetchVatTransactions}>
                    <Eye size={16} className="mr-2" /> View Transactions
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const params = new URLSearchParams({ period_start: vatPeriodStart, period_end: vatPeriodEnd, inline: 'true' });
                      const res = await axios.get(`${API_URL}/tax/vat-return/pdf?${params}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      window.open(url, '_blank');
                    }}
                    data-testid="view-vat-return-pdf"
                  >
                    <Eye size={16} className="mr-2" /> Preview PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const params = new URLSearchParams({ period_start: vatPeriodStart, period_end: vatPeriodEnd });
                      const res = await axios.get(`${API_URL}/tax/vat-return/pdf?${params}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `VAT_Return_${vatPeriodStart}_${vatPeriodEnd}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('VAT Return PDF downloaded');
                    }}
                    data-testid="download-vat-return-pdf"
                  >
                    <Download size={16} className="mr-2" /> PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const params = new URLSearchParams({ period_start: vatPeriodStart, period_end: vatPeriodEnd });
                      const res = await axios.get(`${API_URL}/tax/vat-return/excel?${params}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `VAT_Return_${vatPeriodStart}_${vatPeriodEnd}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('VAT Return Excel downloaded');
                    }}
                    data-testid="download-vat-return-excel"
                  >
                    <Download size={16} className="mr-2" /> Excel
                  </Button>
                  {validationSettings?.enable_vat_validation && (
                    <Button 
                      variant="secondary" 
                      onClick={runVatValidation}
                      disabled={periodStatus?.is_locked}
                      data-testid="validate-vat-btn"
                    >
                      <ShieldCheck size={16} className="mr-2" /> Validate & File
                    </Button>
                  )}
                  {!validationSettings?.enable_vat_validation && (
                    <Button 
                      onClick={() => {
                        setFilingTaxType('vat');
                        setShowFilingDialog(true);
                      }}
                      disabled={periodStatus?.is_locked}
                    >
                      <FileText size={16} className="mr-2" /> File VAT Return
                    </Button>
                  )}
                </>
              )}
            </div>
            {periodStatus?.is_locked && (
              <div className="mt-3 p-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-center gap-2">
                <Lock size={16} className="text-amber-600" />
                <span className="text-sm text-amber-700 dark:text-amber-400">
                  This period has been filed on {formatDate(periodStatus.filing?.filed_at)}
                </span>
              </div>
            )}
          </motion.div>

          {vatReturn && (
            <>
              {/* VAT Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-tactile rounded-lg p-5 border-l-4 border-l-blue-500"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <TrendUp size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Output VAT (Sales)</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(vatReturn.output_vat?.total_vat || 0)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="card-tactile rounded-lg p-5 border-l-4 border-l-amber-500"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <TrendDown size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Input VAT (Purchases)</p>
                      <p className="text-xl font-bold text-amber-600">{formatCurrency(vatReturn.input_vat?.total_vat || 0)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 ${vatReturn.vat_due ? 'border-l-red-500' : 'border-l-emerald-500'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${vatReturn.vat_due ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
                      <Calculator size={20} className={vatReturn.vat_due ? 'text-red-600' : 'text-emerald-600'} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {vatReturn.vat_due ? 'VAT Payable' : 'VAT Refundable'}
                      </p>
                      <p className={`text-xl font-bold ${vatReturn.vat_due ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(Math.abs(vatReturn.net_vat_payable || 0))}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* VAT Return Form Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-tactile rounded-lg overflow-hidden"
              >
                <div className="p-4 border-b bg-[#1e3a5f] text-white">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText size={18} />
                    VAT Return Form 201 Preview
                  </h3>
                  <p className="text-sm text-blue-200 mt-1">
                    Period: {formatDate(vatReturn.period?.start_date)} - {formatDate(vatReturn.period?.end_date)}
                  </p>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* VAT Breakdown Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">VAT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Standard Rated Sales (5%)</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.output_vat?.sales?.standard_rated?.amount || 0)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.output_vat?.sales?.standard_rated?.vat || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Zero Rated Sales</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.output_vat?.sales?.zero_rated?.amount || 0)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Exempt Sales</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.output_vat?.sales?.exempt?.amount || 0)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(0)}</TableCell>
                      </TableRow>
                      <TableRow className="border-t-2">
                        <TableCell className="font-bold">Total Output VAT</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(vatReturn.output_vat?.total_taxable || 0)}</TableCell>
                        <TableCell className="text-right font-bold text-blue-600">{formatCurrency(vatReturn.output_vat?.total_vat || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Standard Rated Purchases</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.input_vat?.purchases?.standard_rated?.amount || 0)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(vatReturn.input_vat?.purchases?.standard_rated?.vat || 0)}</TableCell>
                      </TableRow>
                      <TableRow className="border-t-2">
                        <TableCell className="font-bold">Total Input VAT</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(vatReturn.input_vat?.total_taxable || 0)}</TableCell>
                        <TableCell className="text-right font-bold text-amber-600">{formatCurrency(vatReturn.input_vat?.total_vat || 0)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-bold text-lg" colSpan={2}>Net VAT {vatReturn.vat_due ? 'Payable' : 'Refundable'}</TableCell>
                        <TableCell className={`text-right font-bold text-lg ${vatReturn.vat_due ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formatCurrency(Math.abs(vatReturn.net_vat_payable || 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </motion.div>
            </>
          )}
        </TabsContent>

        {/* Corporate Tax Tab */}
        <TabsContent value="corporate-tax" className="mt-6 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-4"
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Fiscal Year Start</Label>
                <Input
                  type="date"
                  value={fiscalYearStart}
                  onChange={(e) => setFiscalYearStart(e.target.value)}
                  className="w-40"
                  data-testid="ct-year-start"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Fiscal Year End</Label>
                <Input
                  type="date"
                  value={fiscalYearEnd}
                  onChange={(e) => setFiscalYearEnd(e.target.value)}
                  className="w-40"
                  data-testid="ct-year-end"
                />
              </div>
              <Button onClick={fetchCorporateTax} disabled={loading} data-testid="calculate-ct-btn">
                {loading ? <CircleNotch className="animate-spin" size={16} /> : <Calculator size={16} className="mr-2" />}
                Calculate Corporate Tax
              </Button>
              {corporateTax && (
                <>
                  {validationSettings?.enable_corporate_tax_validation && (
                    <Button 
                      variant="secondary" 
                      onClick={runCorporateTaxValidation}
                      disabled={periodStatus?.is_locked}
                      data-testid="validate-ct-btn"
                    >
                      <ShieldCheck size={16} className="mr-2" /> Validate & File
                    </Button>
                  )}
                  {!validationSettings?.enable_corporate_tax_validation && (
                    <Button 
                      onClick={() => {
                        setFilingTaxType('corporate_tax');
                        setShowFilingDialog(true);
                      }}
                      disabled={periodStatus?.is_locked}
                    >
                      <FileText size={16} className="mr-2" /> File Corporate Tax
                    </Button>
                  )}
                </>
              )}
            </div>
          </motion.div>

          {corporateTax && (
            <>
              {/* Corporate Tax Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-tactile rounded-lg p-5"
                >
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Revenue</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(corporateTax.income_statement.total_revenue)}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="card-tactile rounded-lg p-5"
                >
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Expenses</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(corporateTax.income_statement.total_expenses)}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="card-tactile rounded-lg p-5"
                >
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Taxable Income</p>
                  <p className="text-xl font-bold">{formatCurrency(corporateTax.tax_calculation.taxable_income)}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={`card-tactile rounded-lg p-5 ${corporateTax.tax_calculation.tax_liability > 0 ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-emerald-500'}`}
                >
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Tax Liability</p>
                  <p className={`text-xl font-bold ${corporateTax.tax_calculation.tax_liability > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(corporateTax.tax_calculation.tax_liability)}
                  </p>
                </motion.div>
              </div>

              {/* Tax Calculation Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-tactile rounded-lg p-6"
              >
                <h3 className="font-semibold mb-4">UAE Corporate Tax Calculation</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span>Taxable Income</span>
                    <span className="font-medium">{formatCurrency(corporateTax.tax_calculation.taxable_income)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Tax Exempt Threshold (AED 375,000)</span>
                    <span className="font-medium">{formatCurrency(375000)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Amount Above Threshold</span>
                    <span className="font-medium">{formatCurrency(Math.max(0, corporateTax.tax_calculation.taxable_income - 375000))}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Tax Rate</span>
                    <span className="font-medium">{corporateTax.tax_calculation.applicable_rate}%</span>
                  </div>
                  <div className="flex justify-between py-2 bg-muted/50 -mx-6 px-6 rounded">
                    <span className="font-bold">Corporate Tax Liability</span>
                    <span className="font-bold text-lg">{formatCurrency(corporateTax.tax_calculation.tax_liability)}</span>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </TabsContent>

        {/* Filings Tab */}
        <TabsContent value="filings" className="mt-6 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Filing History</h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchFilings}>
                  <MagnifyingGlass size={16} className="mr-2" /> Load Filings
                </Button>
                <Button variant="outline" onClick={() => setShowDeadlineDialog(true)}>
                  <Plus size={16} className="mr-2" /> Add Deadline
                </Button>
              </div>
            </div>
          </motion.div>

          {filings.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-tactile rounded-lg overflow-hidden"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Filed By</TableHead>
                    <TableHead>Filed At</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filings.map((filing) => (
                    <TableRow key={filing.id}>
                      <TableCell>
                        <Badge variant="outline">{filing.tax_type.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(filing.period_start)} - {formatDate(filing.period_end)}</TableCell>
                      <TableCell>{filing.filed_by_name}</TableCell>
                      <TableCell>{formatDate(filing.filed_at)}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Lock size={12} className="mr-1" /> Filed
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </motion.div>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-6"
          >
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Receipt size={20} />
              VAT Registration Details
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Registration Number (TRN)</Label>
                <Input
                  value={vatSettings.trn}
                  onChange={(e) => setVatSettings(p => ({ ...p, trn: e.target.value }))}
                  placeholder="100-XXXX-XXXX-XXX"
                  data-testid="settings-trn"
                />
              </div>
              <div className="space-y-2">
                <Label>Filing Frequency</Label>
                <Select 
                  value={vatSettings.vat_filing_frequency}
                  onValueChange={(v) => setVatSettings(p => ({ ...p, vat_filing_frequency: v }))}
                >
                  <SelectTrigger data-testid="settings-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border sm:col-span-2">
                <div>
                  <p className="font-medium">VAT Registered</p>
                  <p className="text-sm text-muted-foreground">Is the company registered for UAE VAT?</p>
                </div>
                <Switch
                  checked={vatSettings.vat_registered}
                  onCheckedChange={(v) => setVatSettings(p => ({ ...p, vat_registered: v }))}
                  data-testid="settings-vat-registered"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border sm:col-span-2">
                <div>
                  <p className="font-medium">Free Zone Entity</p>
                  <p className="text-sm text-muted-foreground">Is the company operating in a UAE Free Zone?</p>
                </div>
                <Switch
                  checked={vatSettings.is_free_zone}
                  onCheckedChange={(v) => setVatSettings(p => ({ ...p, is_free_zone: v }))}
                  data-testid="settings-free-zone"
                />
              </div>
              {vatSettings.is_free_zone && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Free Zone Name</Label>
                  <Input
                    value={vatSettings.free_zone_name}
                    onChange={(e) => setVatSettings(p => ({ ...p, free_zone_name: e.target.value }))}
                    placeholder="e.g., DMCC, JAFZA, DIFC"
                    data-testid="settings-free-zone-name"
                  />
                </div>
              )}
            </div>
            
            <div className="mt-6 pt-4 border-t flex justify-end">
              <Button onClick={saveVatSettings} disabled={loading} data-testid="save-vat-settings-btn">
                {loading ? <CircleNotch className="animate-spin mr-2" size={16} /> : null}
                Save VAT Settings
              </Button>
            </div>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Filing Dialog */}
      <Dialog open={showFilingDialog} onOpenChange={setShowFilingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              File {filingTaxType === 'vat' ? 'VAT' : 'Corporate Tax'} Return
            </DialogTitle>
            <DialogDescription>
              Review and confirm before filing. This action will lock the tax period.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm">
                <strong>Period:</strong> {filingTaxType === 'vat' ? 
                  `${formatDate(vatPeriodStart)} - ${formatDate(vatPeriodEnd)}` :
                  `${formatDate(fiscalYearStart)} - ${formatDate(fiscalYearEnd)}`
                }
              </p>
            </div>
            
            {/* Compliance Disclaimer */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Compliance Disclaimer</p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                    This system provides validation checks to reduce errors. Final responsibility for accuracy 
                    and compliance remains with the business. Please verify all figures before submission.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Warnings Acknowledgment */}
            {(vatValidationResult?.warnings?.length > 0 || ctValidationResult?.warnings?.length > 0) && (
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox 
                  id="acknowledge" 
                  checked={acknowledgedWarnings}
                  onCheckedChange={setAcknowledgedWarnings}
                />
                <label htmlFor="acknowledge" className="text-sm">
                  I acknowledge there are warnings and wish to proceed with filing
                </label>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={filingNotes}
                onChange={(e) => setFilingNotes(e.target.value)}
                placeholder="Any notes for this filing..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFilingDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleFileTaxReturn} 
              disabled={filing || (getValidationResult()?.warnings?.length > 0 && !acknowledgedWarnings)}
            >
              {filing ? <CircleNotch className="animate-spin mr-2" size={16} /> : <Lock size={16} className="mr-2" />}
              Confirm & File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deadline Dialog */}
      <Dialog open={showDeadlineDialog} onOpenChange={setShowDeadlineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filing Deadlines</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {upcomingDeadlines.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Upcoming Deadlines</h4>
                {upcomingDeadlines.map((d) => (
                  <div key={d.id} className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                    <div>
                      <Badge variant="outline">{d.tax_type.toUpperCase()}</Badge>
                      <p className="text-sm mt-1">{formatDate(d.period_start)} - {formatDate(d.period_end)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatDate(d.deadline_date)}</p>
                      <p className="text-xs text-muted-foreground">{d.reminder_days} days reminder</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Add New Deadline</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tax Type</Label>
                  <Select value={newDeadline.tax_type} onValueChange={(v) => setNewDeadline(p => ({...p, tax_type: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vat">VAT</SelectItem>
                      <SelectItem value="corporate_tax">Corporate Tax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Deadline Date</Label>
                  <Input 
                    type="date" 
                    value={newDeadline.deadline_date}
                    onChange={(e) => setNewDeadline(p => ({...p, deadline_date: e.target.value}))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Period Start</Label>
                  <Input 
                    type="date" 
                    value={newDeadline.period_start}
                    onChange={(e) => setNewDeadline(p => ({...p, period_start: e.target.value}))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Period End</Label>
                  <Input 
                    type="date" 
                    value={newDeadline.period_end}
                    onChange={(e) => setNewDeadline(p => ({...p, period_end: e.target.value}))}
                  />
                </div>
              </div>
              <Button className="w-full mt-3" onClick={createDeadline}>
                <Plus size={16} className="mr-2" /> Create Deadline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>VAT Transactions</DialogTitle>
          </DialogHeader>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>VAT Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">VAT</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((t, idx) => (
                <TableRow key={idx}>
                  <TableCell>{formatDate(t.date)}</TableCell>
                  <TableCell>{t.document_number}</TableCell>
                  <TableCell>
                    <Badge variant={t.type === 'sale' ? 'default' : 'secondary'}>
                      {t.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{t.vat_category}</TableCell>
                  <TableCell className="text-right">{formatCurrency(t.amount)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(t.vat)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
